import { ParsedQuery } from 'query-string'
import {
  yyyyMmDd,
  subDays,
  isEmpty,
  format as formatDate,
  dateFormatterWithTimezone,
  zonedTimeToUtc,
  dateFormatterLocal
} from '@fareye/utils'
import { Parser } from 'json2csv'
import FileSaver from 'file-saver'
import TimezoneAbbr from '../modules/Timezone/timezoneAbbr.json'
import { IDateRange } from '@fareye/types'
import { TIMEZONE_KEY } from '../config/constants'
import { i18n } from '../hooks/usei18n'
import { IDownloadToCSV } from './types'

export interface IDateInitializer {
  showDatePicker: boolean
  carryDate: boolean
  searchParams: ParsedQuery<Date | string>
  daysDiff?: number
}

export const setDateFromStorage = ({
  carryDate,
  searchParams,
  daysDiff = 1,
  showDatePicker
}: IDateInitializer): IDateRange<Date> => {
  if (showDatePicker) {
    // Need to convert return type of date functions from string to Date
    let prevStartDate: Date | string = yyyyMmDd(subDays(new Date(), daysDiff))
    let prevEndDate: Date | string = yyyyMmDd()
    if (carryDate && searchParams.startDate && searchParams.endDate) {
      prevStartDate = searchParams.startDate as Date
      prevEndDate = searchParams.endDate as Date
    } else if (
      carryDate &&
      (!searchParams.startDate || !searchParams.endDate)
    ) {
      const storeDate = (): IDateRange<Date> =>
        JSON.parse(localStorage.getItem('date') || '{}')
      if (!isEmpty(storeDate())) {
        prevStartDate = storeDate().startDate
        prevEndDate = storeDate().endDate
      }
    }
    return { startDate: prevStartDate as Date, endDate: prevEndDate as Date }
  } else {
    return { startDate: new Date(), endDate: new Date() }
  }
}

export const filterDuplicateEntriesFromSelection = (
  selectedFilter: any[],
  value: any[]
) => {
  let newValue = selectedFilter
  if (value) {
    value.forEach((item: any) => {
      if (
        !selectedFilter.find(
          (o: any) => o.label === item.label && o.key === item.key
        )
      ) {
        newValue.push(item)
      }
    })
  }
  return newValue
}

// simplify this unnecessary complex code only after increment
export const selectedfiltersAlreadySaved = (
  selectedFilter: any,
  favList: any
) => {
  // return true means allow saving selectedFilters
  if (selectedFilter && selectedFilter.length) {
    const sameFavListLen: any = favList.filter(
      (o: any) => o.data.filters.length === selectedFilter.length
    )
    if (sameFavListLen.length) {
      for (var i = 0; i < sameFavListLen.length; i++) {
        let dataisSameInFav = false
        const tempSavedFilter = sameFavListLen[i].data.filters
        const uniqFavLabels = Array.from(
          new Set(tempSavedFilter.map((o: any) => o.label))
        )
        const uniqSelectedLabels = Array.from(
          new Set(selectedFilter.map((o: any) => o.label))
        )
        if (uniqSelectedLabels.length === uniqFavLabels.length) {
          let dataIsSame = false
          for (var j = 0; j < uniqFavLabels.length; j++) {
            dataIsSame = uniqSelectedLabels.includes(uniqFavLabels[j])
            if (!dataIsSame) {
              // if selected filter item do not matches saved filter item allow saving filter
              break
            }
            if (j == uniqFavLabels.length - 1 && dataIsSame) {
              // if all filter item from selected filter matches saved filter do not allow save
              dataisSameInFav = true
            }
          }
        } else if (i == sameFavListLen.length - 1) {
          // if selected filters length do not matches saved filter length allow saving filter
          if (dataisSameInFav) {
            return false
          }
          return true
        }
      }
    } else {
      // if there is selected filter but not in saved filters allow saving filter
      return true
    }
  } else {
    // if there is no selected filter from search dont allow saving filter
    return false
  }
}

export const dateFormatter = dateFormatterLocal

export const getTimezone = () => {
  let pickTimezoneFromStorage: any = localStorage.getItem(TIMEZONE_KEY)
  pickTimezoneFromStorage = pickTimezoneFromStorage
    ? JSON.parse(pickTimezoneFromStorage).value
    : Intl.DateTimeFormat().resolvedOptions().timeZone
  return pickTimezoneFromStorage
}

export const zonedToUTC = (time: any) => {
  const pickTimezoneFromStorage = localStorage.getItem(TIMEZONE_KEY)
  if (pickTimezoneFromStorage) {
    let { value = '' } = JSON.parse(pickTimezoneFromStorage)
    if (value) {
      return zonedTimeToUtc(time, value)
    } else {
      return zonedTimeToUtc(time, 'GMT')
    }
  } else {
    return new Date(time)
  }
}

export const zonedToUTCAdd1Day = (time: any) => {
  let date = zonedToUTC(time)
  // add a day
  date.setDate(date.getDate() + 1)
  return date
}

/**
Accepts array of Data and a file name  
*/
export const downloadToCSV = (info: IDownloadToCSV) => {
  const {
    data,
    options,
    fileName,
    type = 'text/csv;charset=utf-8',
    extension = 'csv'
  } = info
  const parser = new Parser(options)
  const csv: any = type === 'text/zip' ? data : parser.parse(data)
  let csvTranslated
  if (type !== 'text/zip') {
    let lines = csv.split('\n')
    lines[0] = lines[0]
      .split(',')
      .map(
        (word: string) =>
          '"' +
          `${i18n.t(word.replace(/"/g, '')) || word.replace(/"/g, '')}` +
          '"'
      )
    csvTranslated = lines.join('\n')
  } else {
    csvTranslated = csv
  }
  const blob = new Blob([csvTranslated], { type })
  FileSaver.saveAs(blob, `${fileName}.${extension}`)
}
// Parse temperature which can be number or an array.

export function parseTemperature(
  temp: number | string | Array<{ temperature: string; value: number | string }>
) {
  let tempString = ''
  if (Array.isArray(temp)) {
    tempString = temp
      .map(ele => {
        let val = ele?.temperature
        let res = !isNaN(Number(val)) ? Number(val).toFixed(2) : '-'
        return res
      })
      .join(' , ')
  } else {
    tempString =
      temp !== null &&
      temp !== undefined &&
      !(
        typeof temp === 'string' &&
        (temp.trim().length === 0 || isNaN(Number(temp)))
      )
        ? Number(temp).toFixed(2)
        : ''
  }
  return tempString
}
export function parseDoorData(doors: any) {
  return doors?.map((ele: any) => (ele.doorStatus ? 'O' : 'C')).join(' , ')
}
export function parseVoltage(
  loadsensor: boolean | string,
  voltage: number | string
) {
  const loadsensorFlag =
    typeof loadsensor === 'string' ? loadsensor === 'true' : loadsensor
  return loadsensorFlag
    ? voltage !== null &&
      voltage !== undefined &&
      !(
        typeof voltage === 'string' &&
        (voltage.trim().length === 0 || isNaN(Number(voltage)))
      )
      ? Number(voltage).toFixed(2)
      : ''
    : ''
}

// Parse humidity which can be number or string.

export function parseHumidity(humidity: number | string) {
  let humidityString = ''
  humidityString =
    humidity !== null &&
    humidity !== undefined &&
    !(
      typeof humidity === 'string' &&
      (humidity.trim().length === 0 || isNaN(Number(humidity)))
    )
      ? Number(humidity).toFixed(2)
      : ''
  return humidityString
}

// Parse individual door statuses (left_door, right_door, back_door)

export function parseIndividualDoors(others: {
  left_door?: string
  right_door?: string
  back_door?: string
  leftDoor?: string
  rightDoor?: string
  backDoor?: string
}): { leftDoor?: string; rightDoor?: string; backDoor?: string } {
  const result: { leftDoor?: string; rightDoor?: string; backDoor?: string } = {}

  if (others?.left_door) {
    result.leftDoor = others.left_door
  } else if (others?.leftDoor) {
    result.leftDoor = others.leftDoor
  }
  if (others?.right_door) {
    result.rightDoor = others.right_door
  } else if (others?.rightDoor) {
    result.rightDoor = others.rightDoor
  }
  if (others?.back_door) {
    result.backDoor = others.back_door
  } else if (others?.backDoor) {
    result.backDoor = others.backDoor
  }

  return result
}